const CLOUD_SYNC_SERVICE_WORKER_URL = `${process.env.BASE_URL || '/'}cloud-sync-sw.js`;
const CLOUD_SYNC_SERVICE_WORKER_SCOPE = process.env.BASE_URL || '/';
const CLOUD_SYNC_REQUEST_TIMEOUT = 60000;

let registrationPromise = null;

const FATAL_ERROR_CODES = new Set([
  'CLOUD_SYNC_UNSUPPORTED',
  'CLOUD_SYNC_REGISTRATION_FAILED',
  'CLOUD_SYNC_WORKER_INACTIVE',
  'CLOUD_SYNC_REQUEST_TIMEOUT',
  'UNSUPPORTED_WORKER_ENV'
]);

const createCloudSyncError = (code, message, cause) => {
  const error = new Error(message);
  error.code = code;
  if (cause) {
    error.cause = cause;
  }
  return error;
};

const getCloudSyncSupportIssues = () => {
  const issues = [];

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    issues.push('browser runtime');
    return issues;
  }

  if (!('serviceWorker' in navigator)) {
    issues.push('Service Worker');
  }
  if (typeof MessageChannel === 'undefined') {
    issues.push('MessageChannel');
  }
  if (typeof indexedDB === 'undefined') {
    issues.push('IndexedDB');
  }

  return issues;
};

const isCloudSyncServiceWorkerSupported = () => {
  return getCloudSyncSupportIssues().length === 0;
};

export const registerCloudSyncServiceWorker = async () => {
  const supportIssues = getCloudSyncSupportIssues();
  if (supportIssues.length > 0) {
    throw createCloudSyncError(
      'CLOUD_SYNC_UNSUPPORTED',
      `Cloud sync requires ${supportIssues.join(', ')} support.`
    );
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(CLOUD_SYNC_SERVICE_WORKER_URL, {
        scope: CLOUD_SYNC_SERVICE_WORKER_SCOPE,
        updateViaCache: 'none'
      })
      .then(() => navigator.serviceWorker.ready)
      .catch((error) => {
        registrationPromise = null;
        throw createCloudSyncError(
          'CLOUD_SYNC_REGISTRATION_FAILED',
          'Cloud sync Service Worker registration failed.',
          error
        );
      });
  }

  return registrationPromise;
};

export const requestCloudSyncServiceWorker = async (type, payload, options = {}) => {
  const registration = await registerCloudSyncServiceWorker();
  const worker = registration.active || navigator.serviceWorker.controller || registration.waiting;

  if (!worker) {
    throw createCloudSyncError(
      'CLOUD_SYNC_WORKER_INACTIVE',
      'Cloud sync Service Worker is not active yet.'
    );
  }

  const timeoutMs = options.timeoutMs || CLOUD_SYNC_REQUEST_TIMEOUT;

  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timeoutId = setTimeout(() => {
      channel.port1.close();
      reject(
        createCloudSyncError(
          'CLOUD_SYNC_REQUEST_TIMEOUT',
          `Cloud sync Service Worker request timed out after ${timeoutMs}ms.`
        )
      );
    }, timeoutMs);

    channel.port1.onmessage = (event) => {
      clearTimeout(timeoutId);
      channel.port1.close();

      const response = event.data || {};
      if (response.ok === false) {
        reject(
          createCloudSyncError(
            response.code || 'CLOUD_SYNC_REQUEST_FAILED',
            response.error || 'Cloud sync Service Worker request failed.'
          )
        );
        return;
      }

      resolve(response);
    };

    worker.postMessage({ type, payload }, [channel.port2]);
  });
};

export const computeCloudBackupHash = async (backupData) => {
  const response = await requestCloudSyncServiceWorker('compute-backup-hash', { backupData });
  return response.contentHash || '';
};

export const isCloudSyncFatalError = (error) => {
  return !!(error && error.code && FATAL_ERROR_CODES.has(error.code));
};

export { isCloudSyncServiceWorkerSupported };
